import { isArray } from 'vega-util';
import { isColorChannel } from '../../channel';
import { title as fieldDefTitle, valueArray } from '../../channeldef';
import { isContinuousToContinuous } from '../../scale';
import { contains, getFirstDefined } from '../../util';
import { isSignalRef } from '../../vega.schema';
import { guideFormat, guideFormatType } from '../format';
import { getFirstConditionValue } from './encode';
export var legendRules = {
  direction: function direction(_ref) {
    var _direction = _ref.direction;
    return _direction;
  },
  format: function format(_ref2) {
    var fieldOrDatumDef = _ref2.fieldOrDatumDef,
        legend = _ref2.legend,
        config = _ref2.config;
    var format = legend.format,
        formatType = legend.formatType;
    return guideFormat(fieldOrDatumDef, fieldOrDatumDef.type, format, formatType, config, false);
  },
  formatType: function formatType(_ref3) {
    var legend = _ref3.legend,
        fieldOrDatumDef = _ref3.fieldOrDatumDef,
        scaleType = _ref3.scaleType;
    var formatType = legend.formatType;
    return guideFormatType(formatType, fieldOrDatumDef, scaleType);
  },
  gradientLength: function gradientLength(params) {
    var _a, _b;

    var legend = params.legend,
        legendConfig = params.legendConfig;
    return (_b = (_a = legend.gradientLength) !== null && _a !== void 0 ? _a : legendConfig.gradientLength) !== null && _b !== void 0 ? _b : defaultGradientLength(params);
  },
  labelOverlap: function labelOverlap(_ref4) {
    var legend = _ref4.legend,
        legendConfig = _ref4.legendConfig,
        scaleType = _ref4.scaleType;

    var _a, _b;

    return (_b = (_a = legend.labelOverlap) !== null && _a !== void 0 ? _a : legendConfig.labelOverlap) !== null && _b !== void 0 ? _b : defaultLabelOverlap(scaleType);
  },
  symbolType: function symbolType(_ref5) {
    var legend = _ref5.legend,
        markDef = _ref5.markDef,
        channel = _ref5.channel,
        encoding = _ref5.encoding;

    var _a;

    return (_a = legend.symbolType) !== null && _a !== void 0 ? _a : defaultSymbolType(markDef.type, channel, encoding.shape, markDef.shape);
  },
  title: function title(_ref6) {
    var fieldOrDatumDef = _ref6.fieldOrDatumDef,
        config = _ref6.config;
    return fieldDefTitle(fieldOrDatumDef, config, {
      allowDisabling: true
    });
  },
  type: function type(_ref7) {
    var legendType = _ref7.legendType,
        scaleType = _ref7.scaleType,
        channel = _ref7.channel;

    if (isColorChannel(channel) && isContinuousToContinuous(scaleType)) {
      if (legendType === 'gradient') {
        return undefined;
      }
    } else if (legendType === 'symbol') {
      return undefined;
    }

    return legendType;
  },
  values: function values(_ref8) {
    var fieldOrDatumDef = _ref8.fieldOrDatumDef,
        legend = _ref8.legend;
    return _values(legend, fieldOrDatumDef);
  }
};

function _values(legend, fieldOrDatumDef) {
  var vals = legend.values;

  if (isArray(vals)) {
    return valueArray(fieldOrDatumDef, vals);
  } else if (isSignalRef(vals)) {
    return vals;
  }

  return undefined;
}

export { _values as values };
export function defaultSymbolType(mark, channel, shapeChannelDef, markShape) {
  var _a;

  if (channel !== 'shape') {
    // use the value from the shape encoding or the mark config if they exist
    var shape = (_a = getFirstConditionValue(shapeChannelDef)) !== null && _a !== void 0 ? _a : markShape;

    if (shape) {
      return shape;
    }
  }

  switch (mark) {
    case 'bar':
    case 'rect':
    case 'image':
    case 'square':
      return 'square';

    case 'line':
    case 'trail':
    case 'rule':
      return 'stroke';

    case 'arc':
    case 'point':
    case 'circle':
    case 'tick':
    case 'geoshape':
    case 'area':
    case 'text':
      return 'circle';
  }
}
export function clipHeight(legendType) {
  if (legendType === 'gradient') {
    return 20;
  }

  return undefined;
}
export function getLegendType(params) {
  var legend = params.legend;
  return getFirstDefined(legend.type, defaultType(params));
}
export function defaultType(_ref9) {
  var channel = _ref9.channel,
      timeUnit = _ref9.timeUnit,
      scaleType = _ref9.scaleType;

  // Following the logic in https://github.com/vega/vega-parser/blob/master/src/parsers/legend.js
  if (isColorChannel(channel)) {
    if (contains(['quarter', 'month', 'day'], timeUnit)) {
      return 'symbol';
    }

    if (isContinuousToContinuous(scaleType)) {
      return 'gradient';
    }
  }

  return 'symbol';
}
export function getDirection(_ref10) {
  var legendConfig = _ref10.legendConfig,
      legendType = _ref10.legendType,
      orient = _ref10.orient,
      legend = _ref10.legend;

  var _a, _b;

  return (_b = (_a = legend.direction) !== null && _a !== void 0 ? _a : legendConfig[legendType ? 'gradientDirection' : 'symbolDirection']) !== null && _b !== void 0 ? _b : defaultDirection(orient, legendType);
}
export function defaultDirection(orient, legendType) {
  switch (orient) {
    case 'top':
    case 'bottom':
      return 'horizontal';

    case 'left':
    case 'right':
    case 'none':
    case undefined:
      // undefined = "right" in Vega
      return undefined;
    // vertical is Vega's default

    default:
      // top-left / ...
      // For inner legend, uses compact layout like Tableau
      return legendType === 'gradient' ? 'horizontal' : undefined;
  }
}
export function defaultGradientLength(_ref11) {
  var legendConfig = _ref11.legendConfig,
      model = _ref11.model,
      direction = _ref11.direction,
      orient = _ref11.orient,
      scaleType = _ref11.scaleType;
  var gradientHorizontalMaxLength = legendConfig.gradientHorizontalMaxLength,
      gradientHorizontalMinLength = legendConfig.gradientHorizontalMinLength,
      gradientVerticalMaxLength = legendConfig.gradientVerticalMaxLength,
      gradientVerticalMinLength = legendConfig.gradientVerticalMinLength;

  if (isContinuousToContinuous(scaleType)) {
    if (direction === 'horizontal') {
      if (orient === 'top' || orient === 'bottom') {
        return gradientLengthSignal(model, 'width', gradientHorizontalMinLength, gradientHorizontalMaxLength);
      } else {
        return gradientHorizontalMinLength;
      }
    } else {
      // vertical / undefined (Vega uses vertical by default)
      return gradientLengthSignal(model, 'height', gradientVerticalMinLength, gradientVerticalMaxLength);
    }
  }

  return undefined;
}

function gradientLengthSignal(model, sizeType, min, max) {
  var sizeSignal = model.getSizeSignalRef(sizeType).signal;
  return {
    signal: "clamp(".concat(sizeSignal, ", ").concat(min, ", ").concat(max, ")")
  };
}

export function defaultLabelOverlap(scaleType) {
  if (contains(['quantile', 'threshold', 'log'], scaleType)) {
    return 'greedy';
  }

  return undefined;
}